import { markSessionExpired, clearSessionExpiryNotice } from "./sessionExpiryNotice";
import requestHandler from "@/composables/requestHandler";
import crsfHandler from "@/composables/csrfHandler";
import store from "@/store";
import Cookies from "js-cookie";
import { translate } from "./translate";
import cookiesHandler from "./cookiesHandler";
import routeHandler from "./routeHandler";
import { isValidEmail } from "./emailValidation.mjs";

let sessionRestorePromise = null;
let sessionValidated = false;

export default {
  passwordPolicy: {
    minLength: 6,
    maxLength: 64
  },

  async register(form) {
    await crsfHandler.getCRSF(true)
    
    return requestHandler.executeRequest("api/register", form);
  },

  async login(form) {
    await crsfHandler.getCRSF(true)

    form['acceptedCookies'] = cookiesHandler.getEncryptedCookie('cookies-acceptance');
    const response = await requestHandler.executeRequest("api/login", form);
    if(!response.success && response.status === 401){
        response.message = translate('email or password invalid', 'ucfirst');
    }
    if(response.success){
        store.dispatch('saveLoginInformation', response.data);
    }
    return response;
  },

  async loginAndRedirect(form, toUrl = ''){
    await crsfHandler.getCRSF(true)

    const response = await this.login(form)
    if(response.success)
      requestHandler.redirect(toUrl);
  },

  async logout(cleanCacheOnly = false) {
    let response = {success: false, message: ''};
    try{

      if(!cleanCacheOnly){
        response = await requestHandler.executeRequest("api/logout");
      }
      store.dispatch("logout")
      Cookies.remove('XSRF-TOKEN');
      Cookies.remove('jobifull_session');
      Cookies.remove('aut_data');
      Cookies.remove('last_login_as');
      store.dispatch('cleanAuthData');
      sessionValidated = false;
      
      return response;
    }catch(error){
      return response
    }
  },

  /**
   * Restores a persisted bearer session before entering protected routes.
   */
  async attemptLoginByCookies(goTo = ''){
    if(!['','/login','login'].includes(goTo))
      await routeHandler.saveCurrentRoute(goTo);
    return this.restoreSession();
  },

  markSessionValidated(expiresAt = null) {
    clearSessionExpiryNotice();
    sessionValidated = true;
    store.dispatch('saveSessionExpiration', expiresAt);
    requestHandler.resetSessionExpiration();
  },

  async restoreSession() {
    const token = store.getters.getToken;
    if(!token)
      return false;

    const expiresAt = store.getters.getSessionExpiresAt;
    if(expiresAt && Date.parse(expiresAt) <= Date.now()){
      markSessionExpired();
      store.dispatch('saveSessionMessage', {
        message: translate('authentication expired'),
        messageType: 'error'
      });
      await this.logout(true);
      return false;
    }

    if(sessionValidated)
      return store.getters.isLoggedIn;
    if(sessionRestorePromise)
      return sessionRestorePromise;

    store.dispatch('saveIsTyingToLogin', true);
    sessionRestorePromise = (async () => {
      const response = await this.profile({handleUnauthorized: false});
      if(!response.success){
        if(response.status === 401){
          markSessionExpired();
          store.dispatch('saveSessionMessage', {
            message: translate('authentication expired'),
            messageType: 'error'
          });
        }
        await this.logout(true);
        return false;
      }
      store.dispatch('saveLoginInformation', response.data);
      if(response.data.person.accepted_cookies != null)
        store.dispatch('saveCookiesAccepted', response.data.person.accepted_cookies);
      sessionValidated = true;
      return true;
    })().finally(() => {
      store.dispatch('saveIsTyingToLogin', false);
      sessionRestorePromise = null;
    });

    return sessionRestorePromise;
  },

  async profile(options = {}) {
    return requestHandler.executeRequest("api/profile", {}, 'post', options);
  },

  async forgotPassword(email){
    await crsfHandler.getCRSF();
    return requestHandler.executeRequest("api/password/email", {email: email}, 'post');
  },

  async resetPassword(params = {}){
    const parameters = {
      email: params.email !== undefined ? params.email : '',
      password: params.password !== undefined ? params.password : '',
      password_confirmation: params.password_confirmation !== undefined ? params.password_confirmation : '',
      token: params.token !== undefined ? params.token : '',
    };
    await crsfHandler.getCRSF();
    return requestHandler.executeRequest("api/password/reset", parameters, 'post');
  },

  profileIsValid(profileName = ''){
    if(!store.getters.getLoggedUserProfiles.includes(profileName))
        return false;
    return true;
  },

  checkPassword(password = ''){
    let response = {
      success: false,
      message: ''
    }
    if (password.length < this.passwordPolicy.minLength){
      response.message = translate('password must contain at least 6 characters', 'ucfirst');
      return response;
    }
    if (password.length > this.passwordPolicy.maxLength){
      response.message = translate('password must contain at most 64 characters', 'ucfirst');
      return response;
    }
    const specialCharRegex = /[^A-Za-z0-9]/;
    if(!specialCharRegex.test(password)){
      response.message = translate("invalid password, enter a special character.", 'ucfirst');
      return response;
    }
    const numberRegex = /[0-9]/;
    if(!numberRegex.test(password)){
      response.message = `${translate("invalid password, enter a number", 'ucfirst')}.`;
      return response;
    }
    const upperCasaRegex = /[A-Z]/;
    if(!upperCasaRegex.test(password)){
      response.message = `${translate("invalid password, enter an uppercase letter", 'ucfirst')}.`;
      return response;
    }
    const lowerCaseRegex = /[a-z]/;
    if(!lowerCaseRegex.test(password)){
      response.message = `${translate("invalid password, enter a lowercase letter", 'ucfirst')}.`;
      return response;
    }
    response.success = true;
    return response;
  },

  checkEmail(email = ''){
    return isValidEmail(email);
  }
};
